import React, { createContext, useState, useContext, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from '@/components/ui/sonner';

import LandingPage from '@/pages/LandingPage';
import AURALanding from '@/pages/AURALanding';
import AuthPage from '@/pages/AuthPage';
import IntakeWizard from '@/pages/IntakeWizard';
import IntakeWizardV2 from '@/pages/IntakeWizardV2';
import NaviIntroduction from '@/pages/NaviIntroduction';
import UniverseGenerating from '@/pages/UniverseGenerating';
import RegulatoryUniverse from '@/pages/RegulatoryUniverseNew';
import RegulatoryUniversePage from '@/pages/regulatory-universe/RegulatoryUniversePage';
import UniverseRiskDashboard from '@/pages/regulatory-universe/UniverseRiskDashboard';
import UniverseUpload from '@/pages/UniverseUpload';
import Dashboard from '@/pages/Dashboard';
import UpdatesPage from '@/pages/UpdatesPage';
import DraftLawsPage from '@/pages/DraftLawsPage'; // Old Library (kept for reference)
// RegulatoryLibraryPage replaced by TenantLibraryPage using unified component
import TenantLibraryPage from '@/pages/TenantLibraryPage';
import AuditPlansPage from '@/pages/AuditPlansPage';
import CompanyOverviewPage from '@/pages/CompanyOverviewPage';
import CompanyOverviewQuestionnaire from '@/pages/CompanyOverviewQuestionnaire';
import ComplianceAlertsPage from '@/pages/ComplianceAlertsPage';
import NaviPage from '@/pages/NaviPage';
// MasterLibraryPage and MasterLibraryV3Page replaced by unified component
import MasterLibraryPage from '@/pages/MasterLibraryPage';
import MasterLibraryTaxonomyPage from '@/pages/MasterLibraryTaxonomyPage';
import NaviLogicPage from '@/pages/NaviLogicPage';
import CyberDiagnosticsPage from '@/pages/CyberDiagnosticsPage';
import DiagnosticsHubPage from '@/pages/DiagnosticsHubPage';
import RemediationTrackerPage from '@/pages/RemediationTrackerPage';
import RiskLogPage from '@/pages/RiskLogPage';
import ICTAssessmentPage from '@/pages/ICTAssessmentPage';
import IntegrationsSettingsPage from '@/pages/IntegrationsSettingsPage';
import IntegrationHubPage from '@/pages/IntegrationHubPage';
import AuditIntelligencePage from '@/pages/AuditIntelligencePage';
import NavigateAdminPage from '@/pages/NavigateAdminPage';
import GroupSelector from '@/pages/GroupSelector';
import GroupDashboard from '@/pages/GroupDashboard';
import DepartmentWorkflowPage from '@/pages/DepartmentWorkflowPage';
import DocumentGovernancePage from '@/pages/DocumentGovernancePage';
import PolicyLibraryPage from '@/pages/PolicyLibraryPage';
import AdminRegulatoryUniversePage from '@/pages/admin/AdminRegulatoryUniversePage';
import RegulatorySourcesAdmin from '@/pages/RegulatorySourcesAdmin';
import CRMPPage from '@/pages/CRMPPage';
import CRMPV2Page from '@/pages/CRMPV2Page';
import ChangeManagementWorkspace from '@/pages/ChangeManagementWorkspace';
import {
  CybersecurityPage,
  DataProtectionPage,
  FinancialCrimePage,
  TaxCompliancePage,
  CorporateGovernancePage,
  ConsumerProtectionPage,
  EmploymentLabourPage
} from '@/pages/ThemePages';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch {
      // Failed to fetch user - logout
      logout();
    } finally {
      setLoading(false);
    }
  };

  const refreshUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch {
      // Failed to refresh user - silently continue
    }
  };

  const login = (newToken, userData) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gold font-mono">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" />;
  }

  // Allow access to questionnaire and universe generation routes without redirect
  const allowedRoutes = ['/company-questionnaire', '/universe/generating', '/universe/upload', '/navi-intro'];
  const isAllowedRoute = allowedRoutes.some(route => location.pathname.startsWith(route));
  
  // If user hasn't completed OR skipped questionnaire and is trying to access protected routes, redirect
  // Users who skipped can access the dashboard
  const canAccessProtectedRoutes = user.questionnaire_completed || user.questionnaire_skipped;
  if (!canAccessProtectedRoutes && !isAllowedRoute) {
    return <Navigate to="/company-questionnaire" replace />;
  }

  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="min-h-screen bg-background">
          <Routes>
            <Route path="/" element={<AURALanding />} />
            <Route path="/auth" element={<AuthPage />} />
            <Route
              path="/company-questionnaire"
              element={
                <PrivateRoute>
                  <CompanyOverviewQuestionnaire />
                </PrivateRoute>
              }
            />
            <Route
              path="/intake"
              element={
                <PrivateRoute>
                  <IntakeWizardV2 />
                </PrivateRoute>
              }
            />
            <Route
              path="/intake-legacy"
              element={
                <PrivateRoute>
                  <IntakeWizard />
                </PrivateRoute>
              }
            />
            <Route
              path="/navi-intro"
              element={
                <PrivateRoute>
                  <NaviIntroduction />
                </PrivateRoute>
              }
            />
            <Route
              path="/universe/generating"
              element={
                <PrivateRoute>
                  <UniverseGenerating />
                </PrivateRoute>
              }
            />
            <Route
              path="/universe/upload"
              element={
                <PrivateRoute>
                  <UniverseUpload />
                </PrivateRoute>
              }
            />
            <Route
              path="/universe/:universeId"
              element={
                <PrivateRoute>
                  <RegulatoryUniverse />
                </PrivateRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <PrivateRoute>
                  <Dashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/regulatory-universe"
              element={
                <PrivateRoute>
                  <RegulatoryUniversePage />
                </PrivateRoute>
              }
            />
            <Route
              path="/regulatory-universe/risk-dashboard"
              element={
                <PrivateRoute>
                  <UniverseRiskDashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/regulatory-universe/*"
              element={
                <PrivateRoute>
                  <RegulatoryUniversePage />
                </PrivateRoute>
              }
            />
            <Route
              path="/library"
              element={
                <PrivateRoute>
                  <TenantLibraryPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/library-old"
              element={
                <PrivateRoute>
                  <DraftLawsPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/document-governance"
              element={
                <PrivateRoute>
                  <DocumentGovernancePage />
                </PrivateRoute>
              }
            />
            <Route
              path="/policy-library"
              element={
                <PrivateRoute>
                  <PolicyLibraryPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/audit-plans"
              element={
                <PrivateRoute>
                  <AuditPlansPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/company-overview"
              element={
                <PrivateRoute>
                  <CompanyOverviewPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/compliance-alerts"
              element={
                <PrivateRoute>
                  <ComplianceAlertsPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/diagnostics"
              element={
                <PrivateRoute>
                  <DiagnosticsHubPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/diagnostics/cybersecurity"
              element={
                <PrivateRoute>
                  <CyberDiagnosticsPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/diagnostics/ict-assessment"
              element={
                <PrivateRoute>
                  <ICTAssessmentPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/workflow"
              element={
                <PrivateRoute>
                  <DepartmentWorkflowPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/remediation-tracker"
              element={
                <PrivateRoute>
                  <RemediationTrackerPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/risk-log"
              element={
                <PrivateRoute>
                  <RiskLogPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/navi"
              element={
                <PrivateRoute>
                  <NaviPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/settings/integrations"
              element={
                <PrivateRoute>
                  <IntegrationsSettingsPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/integration-hub"
              element={
                <PrivateRoute>
                  <IntegrationHubPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/audit-intelligence"
              element={
                <PrivateRoute>
                  <AuditIntelligencePage />
                </PrivateRoute>
              }
            />
            <Route
              path="/themes/cybersecurity"
              element={
                <PrivateRoute>
                  <CybersecurityPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/themes/data-protection"
              element={
                <PrivateRoute>
                  <DataProtectionPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/themes/financial-crime"
              element={
                <PrivateRoute>
                  <FinancialCrimePage />
                </PrivateRoute>
              }
            />
            <Route
              path="/themes/tax-compliance"
              element={
                <PrivateRoute>
                  <TaxCompliancePage />
                </PrivateRoute>
              }
            />
            <Route
              path="/themes/corporate-governance"
              element={
                <PrivateRoute>
                  <CorporateGovernancePage />
                </PrivateRoute>
              }
            />
            <Route
              path="/themes/consumer-protection"
              element={
                <PrivateRoute>
                  <ConsumerProtectionPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/themes/employment-labour"
              element={
                <PrivateRoute>
                  <EmploymentLabourPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/updates"
              element={
                <PrivateRoute>
                  <UpdatesPage />
                </PrivateRoute>
              }
            />
            {/* /master-library route REMOVED - tenant library is /library using unified component */}
            <Route
              path="/aura-master-library"
              element={
                <PrivateRoute>
                  <MasterLibraryPage />
                </PrivateRoute>
              }
            />
            {/* /master-library-v2 route REMOVED - MasterLibraryV2Page deleted */}
            <Route
              path="/master-library-taxonomy"
              element={
                <PrivateRoute>
                  <MasterLibraryTaxonomyPage />
                </PrivateRoute>
              }
            />
            {/* /master-library-v3 route REMOVED - duplicate of /aura-master-library */}
            <Route
              path="/navi-logic"
              element={
                <PrivateRoute>
                  <NaviLogicPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/navigate-admin"
              element={
                <PrivateRoute>
                  <NavigateAdminPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/regulatory-universe"
              element={
                <PrivateRoute>
                  <AdminRegulatoryUniversePage />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/regulatory-sources"
              element={
                <PrivateRoute>
                  <RegulatorySourcesAdmin />
                </PrivateRoute>
              }
            />
            <Route
              path="/crmp"
              element={
                <PrivateRoute>
                  <CRMPPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/crmp-v2"
              element={
                <PrivateRoute>
                  <CRMPV2Page />
                </PrivateRoute>
              }
            />
            <Route
              path="/change-management"
              element={
                <PrivateRoute>
                  <ChangeManagementWorkspace />
                </PrivateRoute>
              }
            />
            <Route
              path="/groups"
              element={
                <PrivateRoute>
                  <GroupSelector />
                </PrivateRoute>
              }
            />
            <Route
              path="/groups/:groupId/dashboard"
              element={
                <PrivateRoute>
                  <GroupDashboard />
                </PrivateRoute>
              }
            />
          </Routes>
          <Toaster position="top-right" />
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
